import React from "react";
import { action, computed, observable } from "mobx";
import debug from "debug";
import { random } from "lodash";
import ArrowBack from "@material-ui/icons/ArrowBack";

import locale from "@bonliva-crm/locale";
import { UserSelectorStore } from "@containers/UserSelector/UserSelectorStore";
import { ConfirmDialogStore } from "@containers/ConfirmDialog";
import { NodeRef, UserFragment } from "@graphql";

import { BaseStore } from "./BaseStore";
import { Store } from "./Store";
import { environment } from "../../environments/environment";

const log = debug("app:store:UIStore");

type RenderInOverlayCallback = () => React.ReactElement<any>;

export type MainMenuBadgeItem = {
  menu: string;
  badge: string | number | boolean | React.ReactElement<any>;
};

export type TicketTeamSubMenu = {
  teamRef: NodeRef;
  badge: string | number | React.ReactElement<any>;
};

export class UIStore extends BaseStore {
  confirmDialog = new ConfirmDialogStore();
  userSelector = new UserSelectorStore(this._rootStore);
  callListsPopperAnchor = null;
  version: string = environment.version;

  @observable
  confirmLeaveMessage: string = "";
  confirmLeaveCallback: (v: boolean) => void = null;

  historyBlocks: Array<{
    id: string;
    message: string;
    ignoreHashChanges: boolean;
    unregister?: () => void;
  }> = [];

  unblockCb: () => void | null = null;

  @observable private _renderInOverlay: RenderInOverlayCallback = null;

  @observable _mainMenuBadges = {};
  @observable _userTicketTeams = [];

  constructor(private _rootStore: Store) {
    super();

    this.setReady();
  }

  get mainMenuBadges() {
    return this._mainMenuBadges;
  }

  setMainMenuBadges(item: MainMenuBadgeItem) {
    this._mainMenuBadges[item.menu] = item.badge;
  }

  setUserTicketTeams(items: TicketTeamSubMenu[]) {
    this._userTicketTeams = items;
  }

  selectUser = (options: { includeEmpty?: boolean; includeActiveUser?: boolean } = {}) =>
    new Promise<UserFragment>((resolve) =>
      this.userSelector.open({
        includeEmpty: options.includeEmpty,
        includeActiveUser: options.includeActiveUser,
        onSelect: (user) => resolve(user)
      })
    );

  // region Overlay
  @computed get renderInOverlay() {
    return this._renderInOverlay;
  }

  @action.bound openInOverlay(options: { render: RenderInOverlayCallback }) {
    this._renderInOverlay = options.render;
  }

  @action.bound closeOverlay() {
    this._renderInOverlay = null;
  }
  // endregion

  // region Block History
  @action.bound confirmLeave(message, callback: (v: boolean) => void) {
    this.confirmDialog
      .confirm<"ok" | "cancel">((options) => {
        options.title = message ?? locale.AreYouSure;

        options.actions.push({
          title: locale.Leave,
          icon: React.createElement(ArrowBack),
          type: "regular",
          value: "ok"
        });

        options.actions.push({
          title: locale.Stay,
          type: "primary",
          value: "cancel"
        });
      })
      .then((result) => callback(result === "ok"));
  }

  @action.bound confirmDelete(message, callback: (v: boolean) => void) {
    this.confirmDialog
      .confirm<"ok" | "cancel">((options) => {
        options.title = message;

        options.actions.push({
          title: locale.Cancel,
          icon: React.createElement(ArrowBack),
          type: "regular",
          value: "cancel"
        });

        options.actions.push({
          title: locale.Delete,
          type: "primary",
          value: "ok"
        });
      })
      .then((result) => callback(result === "ok"));
  }

  confirmNullifyAsync(message): Promise<boolean> {
    return new Promise((resolve) => this.confirmNullify(message, resolve));
  }

  @action.bound confirmNullify(message, callback: (v: boolean) => void) {
    this.confirmDialog
      .confirm<"ok" | "cancel">((options) => {
        options.title = message;

        options.actions.push({
          title: locale.Cancel,
          icon: React.createElement(ArrowBack),
          type: "regular",
          value: "cancel"
        });

        options.actions.push({
          title: locale.Yes,
          type: "primary",
          value: "ok"
        });
      })
      .then((result) => callback(result === "ok"));
  }

  @action.bound blockHistory(id: string, message: string, ignoreHashChanges?: boolean) {
    log("blockHistory", { id, message, ignoreHashChanges });

    const block = this.historyBlocks.find((b) => b.id === id);

    // if exists, do nothing
    if (block) {
      return;
    }

    this.historyBlocks.push({
      id,
      message,
      ignoreHashChanges: Boolean(ignoreHashChanges)
    });

    this.processBlocks();
  }

  @action.bound unblockHistory(id: string) {
    log("unblockHistory", id);
    this.historyBlocks = this.historyBlocks.filter((b) => b.id !== id);
    this.processBlocks();
  }

  @action.bound clearBlocks() {
    this.historyBlocks = [];
    this.processBlocks();
  }

  @action.bound processBlocks() {
    const { routing } = this._rootStore;

    if (this.unblockCb) {
      if (this.historyBlocks.length === 0) {
        this.unblockCb();
        this.unblockCb = null;
        window.onbeforeunload = null;

        return;
      }

      const block = this.historyBlocks[0];
      const { message } = block;

      if (block.unregister !== this.unblockCb) {
        this.unblockCb();

        const cb = this._rootStore.routing.history.block((tx) => {
          if (window.confirm(message)) {
            cb();
            tx.retry();
          }
        });
        window.onbeforeunload = () => message;

        this.unblockCb = cb;
        this.historyBlocks[0].unregister = cb;
      }
    } else {
      const block = this.historyBlocks[0];

      if (block) {
        log("setting history block", block);

        const { message } = block;

        window.onbeforeunload = () => message;

        const cb = this._rootStore.routing.history.block(({ location }) => {
          // if locations differ - block
          if (location.pathname !== routing.location.pathname) {
            return message;
          }

          // if locations equal, hashes differ and !ignoreHashed - block
          if (
            location.pathname === routing.location.pathname &&
            location.hash !== routing.location.hash &&
            !block.ignoreHashChanges
          ) {
            return message;
          }

          return false;
        });

        this.unblockCb = cb;
        block.unregister = cb;
      }
    }
  }

  @action.bound updateVersion(v: string) {
    if (this.version !== v) {
      if (this.historyBlocks.length === 0) {
        const timeout = random(5, 180) * 1000;
        setTimeout(() => window.location.reload(), timeout);
      }
    } else {
      this.version = v;
    }
  }
  // endregion
}
